"use client";

import { Loader2, Pause, Play } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode, Ref } from "react";
import { cn } from "@/lib/utils";

/**
 * The voice-note row: play/pause button, a waveform slot and the time counter.
 * Shared by the lightweight placeholder (`VoiceNote`) and the live wavesurfer
 * player (`AudioBubblePlayer`) so swapping one for the other changes nothing
 * the visitor can see but the waveform itself.
 *
 * Single-row flex so play button + waveform + time counter share one baseline.
 * `w-60` is the intrinsic target width; `max-w-full` lets it shrink to fit a
 * narrow mobile bubble instead of overflowing the rounded corners (a plain
 * `w-full` would collapse it tiny inside the shrink-wrapped bubble). `min-w-0`
 * lets the flex children shrink rather than force the row wider than its parent.
 */
export function VoiceNoteRow({
  onDark,
  loading,
  isPlaying,
  onPlayPause,
  buttonRef,
  onPointerEnter,
  onFocus,
  wave,
  time,
}: {
  onDark: boolean;
  /** Show the spinner in place of the play glyph. */
  loading: boolean;
  isPlaying: boolean;
  onPlayPause: () => void;
  buttonRef?: Ref<HTMLButtonElement>;
  onPointerEnter?: () => void;
  onFocus?: () => void;
  /** The waveform element; it must carry its own `min-w-0 flex-1`. */
  wave: ReactNode;
  time: string;
}) {
  const t = useTranslations("messages");
  return (
    <div
      className="flex w-60 max-w-full min-w-0 items-center gap-3"
      onPointerEnter={onPointerEnter}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={onPlayPause}
        onFocus={onFocus}
        aria-label={isPlaying ? t("pauseAudio") : t("playAudio")}
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-opacity",
          onDark
            ? "bg-wx-primary-fg text-wx-primary hover:opacity-90"
            : "bg-wx-primary text-wx-primary-fg hover:bg-wx-primary-hover",
        )}
      >
        {loading ? (
          <Loader2 size={12} className="animate-spin" />
        ) : isPlaying ? (
          <Pause size={12} />
        ) : (
          // Optical centering - the play triangle's visual mass sits left of
          // the SVG bounding box.
          <Play size={12} className="translate-x-[1px]" />
        )}
      </button>
      {wave}
      <span
        className={cn(
          "shrink-0 font-mono text-[10px] tabular-nums",
          onDark ? "text-wx-primary-fg/70" : "text-wx-fg-subtle",
        )}
      >
        {time}
      </span>
    </div>
  );
}
